import { useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { FileText } from 'lucide-react';
import { useFieldTemplates } from './FormTemplatesContext';

/** How much of a template's text the picker previews under its name. */
const PREVIEW_LENGTH = 160;

export interface FieldTemplatePickerProps {
  /** Question the surrounding field answers. Templates are keyed by it. */
  qid: string;
  /** The field's current answer, which decides whether loading needs a confirm. */
  currentValue: string;
  /** Called with the chosen template's text, replacing the field's answer. */
  onInsert: (text: string) => void;
  disabled?: boolean;
}

function previewOf(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= PREVIEW_LENGTH) return collapsed;
  return `${collapsed.slice(0, PREVIEW_LENGTH).trimEnd()}...`;
}

/**
 * "Use template" beside a form text field, when the agency has authored any.
 *
 * Renders nothing at all when the field has no templates, which is the common
 * case: an agency templates a handful of questions out of the hundreds a form
 * contains, so this has to be invisible rather than merely empty. That also
 * makes the component safe wherever no template provider is mounted, since the
 * hook returns an empty list there.
 *
 * Loading a template REPLACES the field's answer rather than appending. The
 * text is written to be the answer, so appending it to an existing draft would
 * produce two half-answers in one box. Because that discards whatever was
 * typed, a non-empty field is confirmed first; an empty one loads immediately,
 * since there is nothing to lose and the confirm would only be in the way.
 *
 * Insertion goes through the caller's own change handler, so the answer is
 * committed and autosaved exactly as if it had been typed. Nothing here knows
 * about saving.
 */
export function FieldTemplatePicker({
  qid,
  currentValue,
  onInsert,
  disabled = false,
}: FieldTemplatePickerProps) {
  const templates = useFieldTemplates(qid);
  const [open, setOpen] = useState(false);
  // Which template is awaiting a replace confirmation, if any. Held per
  // template rather than as a single boolean so the prompt names what is about
  // to overwrite the answer.
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (templates.length === 0) return null;

  const hasExistingText = currentValue.trim().length > 0;

  const load = (text: string) => {
    onInsert(text);
    setPendingId(null);
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Drop a half-answered confirmation rather than resurfacing it the next
    // time the picker opens.
    if (!next) setPendingId(null);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          disabled={disabled}
          className='h-auto gap-1 px-1.5 py-0.5 text-xs text-muted-foreground'
          aria-label={`Use template (${templates.length} available)`}
        >
          <FileText className='h-3 w-3' aria-hidden='true' />
          Use template
        </Button>
      </PopoverTrigger>
      {/* Aligned to its end, not its start. The trigger sits at the right edge
          of the field, and a form section can be far wider than the window, so
          opening rightward put the list off the edge of the screen with the
          template names clipped. Ending where the trigger ends opens it back
          over the field instead.

          Width is capped against the viewport as well as fixed, so a narrow
          window gets a narrower list rather than one it has to scroll sideways
          to read.

          Scrolls rather than growing: an agency can author many templates for
          one field, and a popover taller than the viewport would put the
          choices out of reach. */}
      <PopoverContent
        align='end'
        className='w-80 max-w-[calc(100vw-2rem)] max-h-[320px] overflow-y-auto p-1'
      >
        <ul className='space-y-0.5'>
          {templates.map(template => {
            const isPending = pendingId === template.id;
            return (
              <li key={template.id}>
                <button
                  type='button'
                  className='w-full rounded px-2 py-1.5 text-left hover:bg-accent focus:bg-accent focus:outline-none'
                  onClick={() => {
                    if (hasExistingText && !isPending) {
                      setPendingId(template.id);
                      return;
                    }
                    load(template.template_text);
                  }}
                >
                  <span className='block text-sm font-medium'>
                    {template.name}
                  </span>
                  <span className='block text-xs text-muted-foreground'>
                    {previewOf(template.template_text)}
                  </span>
                  {isPending && (
                    <span className='mt-1 block text-xs font-medium text-destructive'>
                      This replaces what is in the box. Click again to confirm.
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
